import { router } from "@inertiajs/react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { useYearContext } from "../../../hooks/useYearContext";
import type { Coach } from "../../../models/coach";
import type { Pagination } from "../../../models/pagination";
import { coach_path, coaches_path, edit_coach_path, new_coach_path } from "../../../routes";
import Users, { resetUserFilter, type UserFilterFormType } from "../Users";

type CoachesProps = {
  coaches: Coach[];
  coach_names: string[];
  filter: {
    name?: string;
    sort_key?: "name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

export default function Coaches({ coaches, filter, coach_names, pagination }: CoachesProps) {
  const { pathWithYearContext } = useYearContext();
  const { sort_key, sort_direction, ...filterFormData } = filter;
  const [isFetching, setIsFetching] = useState(false);

  const sortStatus: DataTableSortStatus<Coach> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };

  const handlePageUpdate = (filters: UserFilterFormType, sortStatus: DataTableSortStatus<Coach>, page: number) => {
    setIsFetching(true);
    router.get(
      pathWithYearContext(coaches_path),
      {
        filter: {
          ...filters,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page: page,
      },
      {
        onFinish: () => setIsFetching(false),
      },
    );
  };

  const onSortChange = (sortStatus: DataTableSortStatus<Coach>) => handlePageUpdate(filterFormData, sortStatus, pagination.page);
  const onFilterSubmit = (values: UserFilterFormType) => handlePageUpdate(values, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  const onView = (coach: Coach) => router.get(pathWithYearContext(coach_path, coach.id));
  const onDelete = (coach: Coach) => router.delete(pathWithYearContext(coach_path, coach.id));
  const onEdit = (coach: Coach) => router.get(pathWithYearContext(edit_coach_path, coach.id));
  const onNew = () => router.get(pathWithYearContext(new_coach_path));

  return (
    <Users<Coach, UserFilterFormType>
      onFilterSubmit={onFilterSubmit}
      filterFormData={filterFormData}
      title="Coaches"
      records={coaches}
      userNames={coach_names}
      onNew={onNew}
      onView={onView}
      onDelete={onDelete}
      onEdit={onEdit}
      sortStatus={sortStatus}
      onSortStatusChange={onSortChange}
      userTypeString="Coach"
      userTypeStringPlural="Coaches"
      onReset={() => onFilterSubmit(resetUserFilter)}
      onPageChange={onPageChange}
      pagination={pagination}
      fetching={isFetching}
    />
  );
}
